import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	Menu,
	MenuItem,
	Checkbox,
	ListItemText,
	Divider,
	Grid,
	Button
} from '@material-ui/core';

import {usePrevious} from "../utils/CustomHooks";
import {isJsonEqual} from "../utils/Json";

const styles = makeStyles(theme => ({
	hideItem: {
		visibility: 'hidden',
		minHeight: 0,
		height: 0,
		padding: 0
	},

	menuItem: {
		backgroundColor: 'transparent !important',
		overflow: "visible",
		whiteSpace: "inherit",
		minHeight: theme.spacing(3.5),
		minWidth: theme.spacing(25),

		"&:hover": {
			backgroundColor: 'transparent !important'
		}
	},

	menuItemFlex: {
		display: 'inline-flex',
		maxWidth: theme.spacing(12.5)
	},

	singleSelectMenuItem: {
		minHeight: theme.spacing(3.5),
		minWidth: theme.spacing(18.75),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3)
	},

	checkbox: {
		padding: 0
	},

	tab: {
		minWidth: theme.spacing(9),
	},

	down: {
		paddingLeft: theme.spacing(2),
		marginLeft: "auto"
	},

	actionButton: {
		margin: theme.spacing(1)
	},

	menu: {
		paddingBottom: 0
	},

	menuPaper: {
		maxWidth: theme.spacing(50)
	},
	button: {
		width: "100%",
		paddingLeft: 15,
		paddingRight: 15
	},
	listItem: {
		display: "flex",
		justifyContent: "flex-start",
	}
}));

function FilterSelect(props) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [selected, setSelected] = useState([]);
	const selectedValue = usePrevious(selected);
	const [allSelected, setAllSelected] = useState(false);
	const [selectedCount, setSelectedCount] = useState(0);
	const [titleString, setTitleString] = useState("");
	const defaults = useRef();

	let classes = styles(),
		{defaultSelections, includeSelectAll, options, className,
			selectAllTitle, idAttr, labelAttr, singleSelect, startIcon,
			name, menuTitle, handleUpdate, shouldReset, includeReset, disabled, singleColumn, ariaLabel} = props;
		//dateRangeIndex = options.length ? options.length + 1 : 0;

	const setupDefaults = useCallback(() => {
		if (defaults.current == null || defaults.current.length === 0) {
			defaults.current = Array.isArray(defaultSelections) ? defaultSelections : defaultSelections.split(",");
			setSelected(defaults.current);
		}
	}, [defaults, defaultSelections]);

	// initial setup
	useEffect(() => {
		if (singleSelect) {
			if (!isJsonEqual(defaultSelections, defaults.current)) {
				setupDefaults();
			}
		} else {
			setupDefaults();
		}
	}, [defaultSelections, defaults, setupDefaults, singleSelect]);

	// when selected changes
	useEffect(() => {
		setSelectedCount(selected.length);
		setAllSelected(selected.length === options.length);
	}, [selected, options, setSelectedCount, setAllSelected]);

	// setup title string to show next to select
	useEffect(() => {
		setTitleString(
			!singleSelect && selectedCount > 0 ? `${menuTitle} (${selectedCount})` : menuTitle
		);
	}, [singleSelect, selectedCount, menuTitle]);

	// call handleUpdate when selected changes
	useEffect(() => {
		let obj = {};
		obj[name] = selected;
		if (selected.length > 0 && selectedValue !== selected &&
			selectedValue !== selected[idAttr] && selectedValue !== selected[labelAttr] &&
			!isJsonEqual(selectedValue, selected)) {
			handleUpdate(name ? obj : selected);
		}
	}, [selected, selectedValue, name, handleUpdate, idAttr, labelAttr]);

	const isSelected = (option) => {
		let {idAttr, labelAttr} = props;

		return selected.includes(option) ||
			selected.find(item => item[idAttr] === option[idAttr] || item === option[idAttr]) ? true : false ||
			selected.find(item => item[labelAttr] === option[labelAttr] || item === option[labelAttr]) ? true : false;
	};

	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleSelectAllClick = (event) => {
		let all = !allSelected;
		setAllSelected(all);
		setSelected(all ? options.map(
			option => option[idAttr] ? option[idAttr] :
				option[labelAttr] ? option[labelAttr] : option
		) : []);
	};

	const handleMenuItemClick = (event, option) => {
		let all = false,
			selectedCopy = Object.assign([], selected);

		if (!singleSelect) {
			if (isSelected(option)) {
				selectedCopy = selected.filter(item => {
					return item !== option &&
						item !== option[idAttr] &&
						item[idAttr] !== option[idAttr] &&
						item !== option[labelAttr] &&
						item[labelAttr] !== option[labelAttr];
				});
			} else {
				selectedCopy.push(
					option[idAttr] ? option[idAttr] :
						option[labelAttr] ? option[labelAttr] : option);
			}

			all = selectedCopy.length > 0 && options.length === selectedCopy.length;
		} else {
			selectedCopy = [];
			selectedCopy.push(
				option[idAttr] ? option[idAttr] :
					option[labelAttr] ? option[labelAttr] : option
			);
			handleClose();
		}

		setSelected(selectedCopy);
		setAllSelected(all);
	};

	const handleReset = useCallback(() => {
		setSelected(defaults.current);
	}, [defaults]);

	useEffect(() => {
		if (shouldReset && typeof shouldReset === "function" && shouldReset()) {
			handleReset();
		}
	}, [defaultSelections, shouldReset, handleReset]);

	const handleClose = () => {
		if (!selected.length && !singleSelect) {
			handleReset();
		}
		setAnchorEl(null);
	};

	let wrapMaxAmount = 6,
		menuItems = (
			<div>
				{includeSelectAll ? (
					<React.Fragment>
						<MenuItem
							key="select-all"
							className={classes.menuItem}
							selected={allSelected}
							onClick={handleSelectAllClick}>
							<Checkbox
								color="primary"
								checked={allSelected}
								className={classes.checkbox}
								inputProps={{
									'aria-label': "Select All"
								}}/>
							<ListItemText>{selectAllTitle || "Select All"}</ListItemText>
						</MenuItem>
						<Divider />
					</React.Fragment>
				) : null}
				{options.map((option, idx) => {
					let selected = isSelected(option);
					return (
						<MenuItem
							key={idx}
							selected={selected}
							onClick={event => handleMenuItemClick(event, option)}
							className={!singleSelect && options.length > wrapMaxAmount && !singleColumn ?
								`${classes.menuItemFlex} ${classes.menuItem}` : !singleSelect ? classes.menuItem : classes.singleSelectMenuItem}>
							{!singleSelect ?
								<Checkbox
									color="primary"
									checked={selected}
									className={classes.checkbox}
									inputProps={{
										'aria-label': option[labelAttr]
									}} /> : null
							}
							<ListItemText>{option[labelAttr]}</ListItemText>
						</MenuItem>
					);
				})}
				{/*includeCustomDates ? (
					<MenuItem
						key="custom-dates"
						selected={selected.indexOf(dateRangeIndex) !== -1}
						onClick={event => this.handleMenuItemClick(event, dateRangeIndex)} >
						<Radio checked={selected.indexOf(dateRangeIndex) !== -1} color="primary"/>
						<ListItemText>{customDatesTitle || "Custom date range"}</ListItemText>
						{
							// TODO: Date fields go here
						}
					</MenuItem>
				) : null*/}
				{!singleSelect ? (
					<React.Fragment>
						<Divider />
						<Grid
							container
							justify="space-between"
							alignItems="center">
							<Grid item>
								{includeReset ?
									<Button
										aria-label="reset"
										color="secondary"
										onClick={handleReset}
										className={classes.actionButton}>
										Reset
									</Button> : null
								}
							</Grid>
							<Grid item>
								<Button
									aria-label="done"
									color="primary"
									onClick={handleClose}
									className={classes.actionButton}>
									Done
								</Button>
							</Grid>
						</Grid>
					</React.Fragment>
				) : null}
			</div>
		);

	return (
		<div className={className}>
			<Button
				className={classes.button}
				aria-label={ariaLabel}
				disabled={disabled}
				startIcon={startIcon}
				onClick={event => handleMenuClick(event)}>
				<ListItemText primary={titleString} className={classes.listItem}/>
				<FontAwesomeIcon className={classes.down} icon={["far", "angle-down"]} />
			</Button>
			
			<Menu
				keepMounted
				onClose={handleClose}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				MenuListProps={{
					classes: {padding: classes.menu}
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				PopoverClasses={{
					paper: classes.menuPaper
				}}>
				{menuItems}
			</Menu>
		</div>
	);
}

FilterSelect.defaultProps = {
	idAttr: "id",
	labelAttr: "name",
	includeReset: true,
	singleColumn: false
};

FilterSelect.propTypes = {
	includeReset: PropTypes.bool,
	options: PropTypes.array.isRequired,
	defaultSelections: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
	handleUpdate: PropTypes.func.isRequired,
	shouldReset: PropTypes.func,
	name: PropTypes.string,
	menuTitle: PropTypes.string,
	includeSelectAll: PropTypes.bool,
	selectAllTitle: PropTypes.string,
	singleSelect: PropTypes.bool,
	singleColumn: PropTypes.bool,
	//useChips: PropTypes.bool,
	//includeCustomDates: PropTypes.bool,
	//customDatesTitle: PropTypes.string,
	//useAlphaGroups: PropTypes.bool,
	className: PropTypes.string,
	idAttr: PropTypes.string,
	labelAttr: PropTypes.string,
	startIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.object]),
	disabled: PropTypes.bool,
	ariaLabel: PropTypes.string
};

export default FilterSelect;